import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { applyAccountLanguage, activeUiLanguage, loadTextOverrides } from '../lib/i18n';
import { supabase } from '../lib/supabaseClient';
import { api } from '../lib/api';

const AuthContext = createContext(null);

const REF_STORAGE_KEY = 'pending_ref';


/**
 * N-98: push the account's RESOLVED language to `users.language`, which is what generation reads.
 *
 * The two were written from different places and could disagree. `LoginForm` sent
 * `i18n.language` immediately after sign-in — before `applyAccountLanguage` had seen the email —
 * so an EN_UI_ACCOUNTS user stored `fa` while the interface rendered English, and every idea came
 * back in Persian inside an English app. That is worse than a plain bug: the app looked correct
 * and lied about which language it was working in.
 *
 * Doing it here instead of at the login call site makes it self-healing — it runs on every session
 * load, so an account whose stored value is already wrong is corrected on the next page load rather
 * than needing a fresh sign-in. Fire-and-forget: a failed reconcile must never block rendering.
 */
function reconcileAccountLanguage() {
  api.setLanguage(activeUiLanguage()).catch(() => {});
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const capturedRef = useRef(false);

  // Capture a ?ref=CODE from the URL on load and persist it until a session exists (survives the
  // email-confirmation / OAuth round-trip). Then fire capture_referral once and clear it.
  useEffect(() => {
    try {
      const code = new URLSearchParams(window.location.search).get('ref');
      if (code) localStorage.setItem(REF_STORAGE_KEY, code);
    } catch (err) { console.warn('referral: read ?ref failed', err); }
  }, []);

  const maybeCaptureReferral = () => {
    if (capturedRef.current) return;
    let code = null;
    try { code = localStorage.getItem(REF_STORAGE_KEY); } catch (err) { console.warn('referral: read pending_ref failed', err); }
    if (!code) return;
    capturedRef.current = true;
    api.out('capture_referral', { code })
      .catch((err) => { console.warn('referral: capture_referral failed', err); })
      .finally(() => { try { localStorage.removeItem(REF_STORAGE_KEY); } catch (err) { console.warn('referral: clear pending_ref failed', err); } });
  };

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
      setLoading(false);
      // Account-scoped UI language (EN_UI_ACCOUNTS): must be applied BEFORE the overrides fetch
      // below, so overrides load for the language the account will actually render in.
      applyAccountLanguage(data.session?.user?.email);
      if (data.session) {
        maybeCaptureReferral();
        // 067: the interface wording an admin has edited. Fetched once, as soon as there is a
        // session to authenticate with, and merged over the shipped locale bundle. Deliberately
        // fire-and-forget — if it fails the app renders its shipped wording rather than not
        // rendering, and the failure is silent because a missing override is not an error.
        loadTextOverrides((action, params) => api.uiText(action, params));
        reconcileAccountLanguage();
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession ?? null);
      applyAccountLanguage(newSession?.user?.email);
      if (newSession) {
        maybeCaptureReferral();
        reconcileAccountLanguage();
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signInWithEmail: (email, password) =>
        supabase.auth.signInWithPassword({ email, password }),
      signUpWithEmail: (email, password, language) =>
        supabase.auth.signUp({
          email,
          password,
          options: language ? { data: { language } } : undefined,
        }),
      signInWithGoogle: () =>
        supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/auth/callback`,
          },
        }),
      signOut: () => supabase.auth.signOut(),
    }),
    [session, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
